/**
 * @file    useDrawerNavigation.ts
 * @scope   SHARED
 * @summary Owns the drawer's view routing: which tab is active and which partition is managed.
 * @consumes components/TabNavigator (Tab type), DiyDrawerMainTab
 * @produces currentTab + setters, goToMainTab, selectedPartition.
 * @sideEffects None. Pure local state; the caller decides what to render for each tab.
 * @notes Subtype sub-view contexts live in each engine plugin's navigation provider
 *        (see DiyWidgetEngine/plugins), so this hook stays subtype-agnostic.
 */
import { useCallback, useState } from 'react';
import { Tab } from 'components/TabNavigator/TabNavigator';
import { PartitionModel } from 'models/Enterprise/DataSourceModel';
import { DiyDrawerMainTab } from '../constants/tabs';

/** Everything the drawer needs from the navigation domain. */
export type UseDrawerNavigationResult = {
  /** Tab currently rendered by the drawer. */
  currentTab: Tab;
  /** Navigates to an arbitrary tab (used by `TabNavigator` and the Back buttons). */
  setCurrentTab: React.Dispatch<React.SetStateAction<Tab>>;
  /** Shortcut back to the root tab. */
  goToMainTab: () => void;
  /** ENGINE-ONLY: partition whose fields are being managed, `null` otherwise. */
  selectedPartition: PartitionModel | null;
  setSelectedPartition: React.Dispatch<React.SetStateAction<PartitionModel | null>>;
};

export const useDrawerNavigation = (): UseDrawerNavigationResult => {
  const [currentTab, setCurrentTab] = useState<Tab>(DiyDrawerMainTab);
  const [selectedPartition, setSelectedPartition] = useState<PartitionModel | null>(null);

  const goToMainTab = useCallback(() => setCurrentTab(DiyDrawerMainTab), []);

  return { currentTab, setCurrentTab, goToMainTab, selectedPartition, setSelectedPartition };
};
